import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, UserPlus, Building2, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import MemberAvatar from '@/components/MemberAvatar';

const API = process.env.REACT_APP_BACKEND_URL;
const get = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => (r.ok ? r.json() : null)).catch(() => null);

const favicon = (website) => {
  if (!website) return null;
  try { return `https://www.google.com/s2/favicons?domain=${new URL(website.startsWith('http') ? website : `https://${website}`).hostname}&sz=128`; }
  catch { return null; }
};
const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : d < 30 ? `${d} days ago` : new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const RailCard = ({ icon: Icon, title, to, testid, children }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-brand-navy" /> {title}
      </h4>
      {to && <Link to={to} className="text-[11px] font-semibold text-brand-navy hover:underline inline-flex items-center gap-0.5" data-testid={`${testid}-see-all`}>See all <ArrowRight className="h-3 w-3" /></Link>}
    </div>
    {children}
  </div>
);

export const NewsHeadlines = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { get('/api/news').then(d => setItems((Array.isArray(d) ? d : d?.news || []).slice(0, 3))); }, []);
  if (!items?.length) return null;
  return (
    <RailCard icon={Newspaper} title="Top headlines" to="/news" testid="rail-news">
      <div className="flex flex-col gap-3">
        {items.map(n => (
          <Link key={n.news_id} to={`/news/${n.news_id}`} className="flex items-start gap-3 group" data-testid={`rail-news-${n.news_id}`}>
            {n.image && (
              <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                <img src={n.image.startsWith('/api') ? `${API}${n.image}` : n.image} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-slate-800 leading-snug line-clamp-2 group-hover:text-brand-navy transition-colors">{n.title}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{n.source || n.author_name} · {ago(n.published_at)}</p>
            </div>
          </Link>
        ))}
      </div>
    </RailCard>
  );
};

export const SuggestedFollows = ({ meId }) => {
  const [items, setItems] = useState(null);
  const [followed, setFollowed] = useState({});
  useEffect(() => {
    Promise.all([get('/api/members/me/matches'), get('/api/members/me/following')]).then(async ([m, f]) => {
      const skip = new Set([...(Array.isArray(f) ? f : []), meId]);
      let picks = (m || []).filter(x => !skip.has(x.user.user_id)).slice(0, 3);
      if (picks.length < 3) {
        // Fallback for thin profiles: members with a photo and job title
        const all = (await get('/api/members')) || [];
        const extra = all.filter(u => !skip.has(u.user_id) && u.picture && u.profile?.role && !picks.some(p => p.user.user_id === u.user_id))
          .sort(() => Math.random() - 0.5).slice(0, 3 - picks.length).map(u => ({ user: u, reasons: [] }));
        picks = [...picks, ...extra];
      }
      setItems(picks);
    });
  }, [meId]);
  const follow = async (u) => {
    const res = await fetch(`${API}/api/members/${u.user_id}/follow`, { method: 'POST', credentials: 'include' });
    if (res.ok) { setFollowed(p => ({ ...p, [u.user_id]: true })); toast.success(`You're now following ${u.name}`); }
    else toast.error('Could not follow');
  };
  if (!items?.length) return null;
  return (
    <RailCard icon={UserPlus} title="Members to follow" to="/members" testid="rail-follow">
      <div className="flex flex-col gap-3">
        {items.map(({ user: u, reasons }) => (
          <div key={u.user_id} className="flex items-center gap-2.5" data-testid={`rail-follow-${u.user_id}`}>
            <Link to={`/members?view=${u.user_id}`} className="flex items-center gap-2.5 min-w-0 flex-1">
              <MemberAvatar name={u.name} picture={u.picture} size={36} />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 truncate">{u.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{reasons?.[0] || [u.profile?.role, u.profile?.organisation].filter(Boolean).join(' · ')}</p>
              </div>
            </Link>
            <button type="button" onClick={() => !followed[u.user_id] && follow(u)} disabled={followed[u.user_id]}
              data-testid={`rail-follow-btn-${u.user_id}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 transition-colors ${followed[u.user_id] ? 'bg-emerald-50 text-emerald-700' : 'bg-brand-navy text-white hover:bg-brand-dark'}`}>
              {followed[u.user_id] ? <span className="inline-flex items-center gap-0.5"><Check className="h-3 w-3" /> Following</span> : 'Follow'}
            </button>
          </div>
        ))}
      </div>
    </RailCard>
  );
};

const NewPartners = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { get('/api/partners?sort=newest&limit=4').then(d => setItems(d?.partners || [])); }, []);
  if (!items?.length) return null;
  return (
    <RailCard icon={Building2} title="Latest partners" to="/partners" testid="rail-partners">
      <div className="flex flex-col gap-3">
        {items.map(p => {
          const logo = p.logo_url || favicon(p.website);
          return (
            <Link key={p.sponsor_id} to={`/partners/${p.sponsor_id}`} className="flex items-center gap-2.5 group" data-testid={`rail-partner-${p.sponsor_id}`}>
              <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {logo ? <img src={logo} alt="" className="w-full h-full object-contain p-1" /> : <Building2 className="h-4 w-4 text-slate-300" />}
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 truncate group-hover:text-brand-navy transition-colors">{p.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{p.sector || 'Partner'}{p.created_at ? ` · Joined ${ago(p.created_at)}` : ''}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </RailCard>
  );
};

export default function DashboardRailWidgets({ user }) {
  return (
    <div className="flex flex-col gap-4" data-testid="dashboard-rail-widgets">
      <NewsHeadlines />
      <SuggestedFollows meId={user?.user_id} />
      <NewPartners />
    </div>
  );
}
